import { Markdown } from '@/components/Markdown'
import type { InstanceDetail, TemplateDetail, TemplateParameter } from '@/lib/scientific'

export function WorkflowInstructions({ data, current, selected, onSelect }: {
  data: TemplateDetail; current: InstanceDetail | null; selected: string; onSelect: (id: string) => void
}) {
  const { definition } = data
  const whole = !selected
  const comparison = whole ? definition.prompt_comparison : current?.prompt_comparison
  const reference = comparison?.title ?? comparison?.task_id
  const taskTitle = data.tasks.find(task => task.task_id === selected)?.title ?? selected
  const removed = current?.prompt_comparison?.removed ?? []
  const additions = definition.prompt_comparison?.additions ?? []
  const showComparison = whole && data.tasks.length > 1 && !!definition.task_comparisons?.length
  return <>
    <section className="box science-document science-prompt">
      <h2>{whole ? 'Task instructions' : `Instructions for ${taskTitle}`}</h2>
      {comparison && <p className="muted small science-instructions-context">{whole
        ? comparison.identical ? (data.tasks.length > 1 ? `All ${data.tasks.length} tasks use these instructions. Each task has its own experiment files and grading.` : 'These are the instructions given to the solver.')
          : `Showing ${reference}. Highlighted wording changes between tasks. Choose a task to read its instructions.`
        : comparison.identical ? `Same instructions as ${reference}. Check this task’s files and rubric for its experiment and expected results.`
          : `Highlighted wording differs from ${reference}.`}</p>}
      <Markdown source={whole ? definition.example_prompt : current?.descriptor.prompt ?? ''} highlights={comparison?.highlights} />
      {whole && !!additions.length && <details className="science-prompt-changes">
        <summary>Additional instructions in other tasks</summary>
        {additions.map((item, index) => <div key={`${item.task_id}:${index}`}><button className="science-task-open" onClick={() => onSelect(item.task_id)}>{item.title}</button><p className="prewrap">{item.text}</p></div>)}
      </details>}
      {!whole && !!removed.length && <details className="science-prompt-changes">
        <summary>Instructions omitted from this task</summary>
        {removed.map((text, index) => <p className="prewrap" key={index}>{text}</p>)}
      </details>}
    </section>
    {showComparison && <section className="box science-document science-instruction-comparison" aria-label="Task comparison">
      <h2>How the tasks differ</h2>
      <p className="muted small">Compared with {definition.prompt_comparison?.title ?? definition.prompt_comparison?.task_id}.</p>
      <div className="table-wrap"><table><thead><tr><th>Task</th><th>Instructions</th><th>Input files</th><th>Grading</th></tr></thead>
        <tbody>{data.tasks.map(task => {
          const changes = definition.task_comparisons?.find(item => item.task_id === task.task_id)
          const isReference = task.task_id === definition.prompt_comparison?.task_id
          return <tr key={task.task_id}>
            <td><button className="science-task-open" onClick={() => onSelect(task.task_id)}>{task.title}</button></td>
            <td>{isReference ? 'Comparison task' : changes ? changes.prompt_changed ? 'Different' : 'Same' : 'Unavailable'}</td>
            <td>{isReference ? '—' : difference(changes?.input_changes, 'file')}</td>
            <td>{isReference ? '—' : difference(changes?.grading_changes, 'check')}</td>
          </tr>
        })}</tbody></table></div>
    </section>}
    <details className="science-technical-details" key={selected || 'workflow'}>
      <summary>Technical details</summary>
      <p className="muted small">Package configuration used to generate or grade the tasks. These values are not instructions for the solver.</p>
      {whole ? <>
        <h3>Values across tasks</h3>
        <ParameterValues parameters={definition.parameters} />
      </> : current && <><h3>Task configuration</h3><pre>{JSON.stringify(current.parameters, null, 2)}</pre></>}
    </details>
  </>
}

function difference(count: number | null | undefined, noun: string) {
  if (count == null) return 'Unavailable'
  return count ? `${count} ${noun}${count === 1 ? ' differs' : 's differ'}` : 'Same'
}

function ParameterValues({ parameters }: { parameters: TemplateParameter[] }) {
  return <dl className="science-field-list">{parameters.map(p => <div key={p.name}>
    <dt><code>{p.name}</code><small className="muted">{p.varies ? 'Changes between tasks' : 'Same across tasks'}</small></dt>
    <dd><span className="science-parameter-values">{p.values.slice(0, 6).map((value, index) => <code key={`${index}:${value}`}>{value}</code>)}{(p.distinct_count ?? p.values.length) > 6 && <small className="muted">+{(p.distinct_count ?? p.values.length) - 6} more</small>}</span></dd>
  </div>)}</dl>
}
